// GRNFormXL - Sprint 44 Fase 3 Vista 9.
// Form contenedor GRN. Header + 3 tabs (basicos, lineas, auditoria) +
// resumen card con conteos. Dato y lineas extraidos en sub-componentes
// para respetar regla 300 (legacy era 346L).
import { useEffect } from 'react';
import { ensureKeyframes, injectCSS } from '../../design/motion';
import useGRNFormXL from './hooks/useGRNFormXL';
import GRNDatosTabXL from './GRNDatosTabXL';
import GRNLineasXL from './GRNLineasXL';
import SendButtons from '../../components/shared/SendButtons';
import openProtectedFile from '../../api/openProtectedFile';
import XLBadge from '../../components/xl/XLBadge';
import XLChip from '../../components/xl/XLChip';
import XLSkeleton from '../../components/xl/XLSkeleton';

const CSS_ID = 'xl-grn-form-css';
const CSS = `
.xl-page { padding: 24px; font-family: var(--xl-font-sans); color: var(--xl-color-text) }
.xl-hdr { display: flex; align-items: flex-start; justify-content: space-between;
  gap: 16px; margin-bottom: 16px; flex-wrap: wrap; }
.xl-hdr__left { display: flex; align-items: center; gap: 14px; min-width: 0 }
.xl-hdr__back {
  font: 500 13px/1 var(--xl-font-sans); color: var(--xl-color-text-secondary);
  background: transparent; border: none; cursor: pointer;
  padding: 6px 8px; border-radius: var(--xl-radius-md);
}
.xl-hdr__back:hover { background: var(--xl-color-surface-alt); color: var(--xl-color-text) }
.xl-hdr__title { margin: 0; font-size: 20px; font-weight: 600; line-height: 1.2 }
.xl-hdr__sub { margin: 2px 0 0; font-size: 12px; color: var(--xl-color-text-secondary) }
.xl-hdr__sub b { color: var(--xl-color-text); font-family: var(--xl-font-mono) }
.xl-hdr__actions { display: flex; gap: 8px; align-items: center; flex-wrap: wrap }
.xl-btn {
  font: 500 13px/1 var(--xl-font-sans); padding: 10px 16px;
  border-radius: var(--xl-radius-md); border: 1px solid transparent; cursor: pointer;
}
.xl-btn:focus-visible { outline: 2px solid var(--xl-color-focus); outline-offset: 2px }
.xl-btn:disabled { opacity: 0.45; cursor: not-allowed }
.xl-btn--primary { background: var(--xl-color-primary); color: var(--xl-color-text-inverse) }
.xl-btn--primary:hover { background: var(--xl-color-primary-hover) }
.xl-btn--ghost {
  background: transparent; color: var(--xl-color-text-secondary);
  border-color: var(--xl-color-border);
}
.xl-btn--ghost:hover { background: var(--xl-color-surface); color: var(--xl-color-text) }

.xl-banner { padding: 10px 14px; margin-bottom: 12px; border-radius: var(--xl-radius-md); font-size: 13px }
.xl-banner--ok  { background: rgba(5,150,105,0.10); color: var(--xl-color-success); border: 1px solid rgba(5,150,105,0.28) }
.xl-banner--err { background: rgba(220,38,38,0.10); color: var(--xl-color-danger);  border: 1px solid rgba(220,38,38,0.28) }

.xl-card {
  background: var(--xl-color-surface); border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg); box-shadow: var(--xl-shadow-sm);
  overflow: hidden; margin-bottom: 16px;
}
.xl-tabs { display: flex; gap: 6px; flex-wrap: wrap; padding: 12px 16px;
  background: var(--xl-color-bg); border-bottom: 1px solid var(--xl-color-border) }
.xl-body { padding: 20px }

.xl-resumen {
  display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px;
  margin-bottom: 16px;
}
@media (max-width: 720px) { .xl-resumen { grid-template-columns: 1fr } }
.xl-resumen__card {
  background: var(--xl-color-surface-alt); border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-md); padding: 12px 14px;
}
.xl-resumen__lbl { font-size: 10px; text-transform: uppercase; letter-spacing: 0.06em;
  color: var(--xl-color-text-muted); font-weight: 600; margin-bottom: 4px }
.xl-resumen__val { font-family: var(--xl-font-mono); font-size: 13px; color: var(--xl-color-text) }
.xl-loading { display: flex; flex-direction: column; gap: 10px }
`;

const TABS = [
  { id: 'basicos',   label: 'Datos basicos' },
  { id: 'lineas',    label: 'Lineas' },
  { id: 'auditoria', label: 'Auditoria' },
];
const ESTADO_VARIANT = {
  borrador: 'neutral',
  recibido_parcial: 'warning',
  recibido_total: 'success',
};
const ESTADO_LABEL = {
  borrador: 'Borrador',
  recibido_parcial: 'Recibido parcial',
  recibido_total: 'Recibido total',
};

function fmtFecha(v) {
  if (!v) return '\u2014';
  try { return new Date(v).toLocaleString('es-CO'); } catch (_) { return String(v); }
}

export default function GRNFormXL() {
  useEffect(() => { ensureKeyframes(); injectCSS(CSS_ID, CSS); }, []);
  const fm = useGRNFormXL();

  if (fm.loading) {
    return (
      <div className="xl-page">
        <div className="xl-loading" aria-busy="true">
          <XLSkeleton variant="line" width="40%" height={20} />
          <XLSkeleton variant="rect" height={120} />
          <XLSkeleton variant="rect" height={220} />
        </div>
      </div>
    );
  }

  const onPdf = async () => {
    if (!fm.isEdit) return;
    try {
      await openProtectedFile('/grn/' + fm.grnId + '/pdf', 'application/pdf');
    } catch (e) { alert('No se pudo abrir el PDF: ' + ((e && e.message) || 'error')); }
  };

  const titulo = fm.isEdit ? ('Editar GRN #' + fm.grnId) : 'Nuevo GRN';

  return (
    <div className="xl-page">
      <header className="xl-hdr">
        <div className="xl-hdr__left">
          <button type="button" className="xl-hdr__back"
            onClick={() => fm.navigate('/grn')}>{'\u2190 Volver'}</button>
          <div>
            <h2 className="xl-hdr__title">{titulo}</h2>
            <p className="xl-hdr__sub">
              <XLBadge variant={ESTADO_VARIANT[fm.form.estado] || 'neutral'} dot>
                {ESTADO_LABEL[fm.form.estado] || fm.form.estado}
              </XLBadge>
              {fm.form.referencia && <> {' \u00b7 '} <b>{fm.form.referencia}</b></>}
              {fm.form.po_referencia && <> {' \u00b7 PO: '} <b>{fm.form.po_referencia}</b></>}
            </p>
          </div>
        </div>
        <div className="xl-hdr__actions">
          {fm.isEdit && (
            <>
              <button type="button" className="xl-btn xl-btn--ghost" onClick={onPdf}>PDF</button>
              <SendButtons endpointBase={'/grn/' + fm.grnId} tipoLabel="GRN"
                defaultEmail={fm.form.proveedor_email}
                defaultTelefono={fm.form.proveedor_telefono} />
            </>
          )}
          <button type="button" className="xl-btn xl-btn--primary"
            onClick={fm.onSave} disabled={fm.saving}>
            {fm.saving ? 'Guardando\u2026' : 'Guardar'}
          </button>
        </div>
      </header>

      {fm.msg && (
        <div className={'xl-banner ' + (fm.msg.kind === 'ok' ? 'xl-banner--ok' : 'xl-banner--err')}
          role={fm.msg.kind === 'ok' ? 'status' : 'alert'}
          onClick={fm.clearMsg}>
          {fm.msg.text}
        </div>
      )}

      <div className="xl-resumen">
        <div className="xl-resumen__card">
          <div className="xl-resumen__lbl">PO origen</div>
          <div className="xl-resumen__val">{fm.form.po_referencia || '\u2014'}</div>
        </div>
        <div className="xl-resumen__card">
          <div className="xl-resumen__lbl">Proveedor</div>
          <div className="xl-resumen__val" style={{ fontFamily: 'inherit' }}>
            {fm.form.proveedor_nombre || '\u2014'}
          </div>
        </div>
        <div className="xl-resumen__card">
          <div className="xl-resumen__lbl">Fecha recepcion</div>
          <div className="xl-resumen__val">{fm.form.fecha_recepcion || '\u2014'}</div>
        </div>
        <div className="xl-resumen__card">
          <div className="xl-resumen__lbl">Lineas</div>
          <div className="xl-resumen__val">{fm.lineas.length}</div>
        </div>
      </div>

      <div className="xl-card">
        <div className="xl-tabs" role="tablist">
          {TABS.map((t) => (
            <XLChip key={t.id} onClick={() => fm.setTab(t.id)} selected={fm.tab === t.id}>
              {t.label}
            </XLChip>
          ))}
        </div>
        <div className="xl-body">
          {fm.tab === 'basicos' && (
            <GRNDatosTabXL form={fm.form} set={fm.set} isEdit={fm.isEdit} />
          )}
          {fm.tab === 'lineas' && (
            fm.isEdit
              ? <GRNLineasXL lineas={fm.lineas} onLocal={fm.onLineaLocal} onSave={fm.onLineaSave} />
              : <p style={{ color: 'var(--xl-color-text-muted)', fontSize: '13px' }}>
                  Selecciona una PO y guarda primero. Las lineas se copian automaticamente del PO.
                </p>
          )}
          {fm.tab === 'auditoria' && (
            fm.isEdit ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '16px' }}>
                <div>
                  <div style={{ fontSize: '10px', color: 'var(--xl-color-text-muted)', textTransform: 'uppercase', marginBottom: '4px' }}>Creado</div>
                  <div style={{ fontFamily: 'var(--xl-font-mono)', fontSize: '13px' }}>
                    {fmtFecha(fm.form.created_at)}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '10px', color: 'var(--xl-color-text-muted)', textTransform: 'uppercase', marginBottom: '4px' }}>Actualizado</div>
                  <div style={{ fontFamily: 'var(--xl-font-mono)', fontSize: '13px' }}>
                    {fmtFecha(fm.form.updated_at)}
                  </div>
                </div>
              </div>
            ) : <p style={{ color: 'var(--xl-color-text-muted)', fontSize: '13px' }}>Aparecera despues de guardar.</p>
          )}
        </div>
      </div>
    </div>
  );
}
